import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Heart } from "lucide-react";
import { donateHref } from "@/config/site";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div", ...rest }) => {
  const reduce = useReducedMotion();
  const Comp = as === "li" ? motion.li : motion.div;
  return (
    <Comp
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

export const MaskLines = ({ lines, delay = 0.25 }) => {
  const reduce = useReducedMotion();
  return lines.map((l, i) => (
    <span key={l.text} className="block overflow-hidden pb-[0.06em]">
      <motion.span
        className={`block ${l.className || ""}`}
        initial={reduce ? false : { y: "108%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, delay: delay + i * 0.16, ease: [0.76, 0, 0.24, 1] }}
      >
        {l.text}
      </motion.span>
    </span>
  ));
};

export const Pending = ({ label, dark = false, testId }) => (
  <span className={`pending-chip ${dark ? "pending-chip-dark" : ""}`} data-testid={testId}>
    Pending: {label}
  </span>
);

export const Val = ({ value, label, dark, href }) =>
  value ? (href ? <a href={href} className="link-underline">{value}</a> : <span>{value}</span>) : <Pending label={label} dark={dark} />;

export const telHref = (p) => `tel:+1${p.replace(/\D/g, "")}`;

const isExternal = (href) => /^https?:\/\//.test(href);

export const SmartLink = ({ to, children, ...rest }) =>
  isExternal(to) ? (
    <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
  ) : (
    <Link to={to} {...rest}>{children}</Link>
  );

export const DonateButton = ({ label = "Donate", variant = "btn-red", monthly = false, href, className = "", testId, icon = true }) => (
  <SmartLink to={href || donateHref(monthly)} className={`btn ${variant} ${className}`} data-testid={testId}>
    {icon && <Heart size={16} aria-hidden="true" />}
    {label}
  </SmartLink>
);

export const CtaLink = ({ to, children, variant = "btn-navy", className = "", testId }) => (
  <SmartLink to={to} className={`btn ${variant} ${className}`} data-testid={testId}>
    {children}
    <ArrowRight size={16} aria-hidden="true" />
  </SmartLink>
);

export const SectionHeading = ({ eyebrow, title, intro, light = false, className = "", as: H = "h2" }) => (
  <Reveal className={`max-w-3xl ${className}`}>
    {eyebrow && <p className={`eyebrow ${light ? "eyebrow-light" : ""}`}>{eyebrow}</p>}
    <H className={`mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] ${light ? "text-paper" : "text-navy"}`}>
      {title}
    </H>
    {intro && <p className={`mt-6 text-base md:text-lg leading-relaxed ${light ? "text-[#C9D4DF]" : "text-ink/80"}`}>{intro}</p>}
  </Reveal>
);
